import React from "react";
import { connect } from "react-redux";
import { navigate } from "gatsby";
import { keyframes } from "@emotion/core";
import { RiLoader4Line } from "react-icons/ri";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";

import Helmet from "../components/Helmet";
import ConfirmDialog from "../components/Dialog/ConfirmDialog";
import * as Input from "../components/Input";
import { updateOrg } from "../state/global";
import isBrowser from "../utils/isBrowser";
import { Permission } from "../utils/permissions";
import { plans, getPlan } from "../utils/plans";
import countries from "../data/countries.json";

const PlanSelector = ({ planId, portalMessage, handleClick }) => {
    const plan = planId ? getPlan(planId) : plans[0];

    return (
        <div css={{
            display: "grid",
            gap: 15,
            gridTemplateColumns: "repeat(auto-fill, minmax(345px, 1fr))",
        }}>
            <div
                css={{
                    width: "100%",
                    padding: 15,
                    display: "inline-block",
                    backgroundColor: "rgba(100, 100, 100, .25)",
                    borderRadius: 3,
                    fontSize: ".8em",
                    boxShadow: "0px 0px 0px 2px rgba(100, 100, 100, .8) inset",
                    transition: "background-color var(--transition-default-time) ease-in-out, box-shadow var(--transition-default-time) ease-in-out",
                }}
            >
                <div css={{ fontWeight: 500 }}>
                    { plan.name }
                </div>
                <div css={{
                    margin: "10px 0",
                    color: "var(--green)",
                    transition: "color var(--transition-default-time) ease-in-out",
                }}>
                    <span><span css={{ fontWeight: 500 }}>${ plan.price }</span> / month</span>
                </div>
                <div css={{
                    fontSize: ".9em",
                    color: "lightgrey",
                }}>
                    { plan.description }
                </div>
            </div>

            <div
                css={{
                    width: "100%",
                    padding: 15,
                    display: "inline-block",
                    backgroundColor: "rgba(100, 100, 100, .2)",
                    borderRadius: 3,
                    fontSize: ".8em",
                    boxShadow: "none",
                    opacity: portalMessage ? .8 : 1,
                    transition: "background-color var(--transition-default-time) ease-in-out, box-shadow var(--transition-default-time) ease-in-out",
                    cursor: portalMessage ? "not-allowed" : "pointer",
                    ":hover": {
                        boxShadow: portalMessage ? "none" : "0px 0px 0px 2px rgba(100, 100, 100, .8) inset",
                        backgroundColor: portalMessage ? "rgba(100, 100, 100, .2)" : "rgba(100, 100, 100, .3)",
                    },
                }}
                onClick={ portalMessage ? () => {} : handleClick }
            >
                <div css={{
                    marginBottom: 10,
                    fontWeight: 500,
                }}>
                    Change Plan
                </div>
                <div css={{
                    margin: "10px 0",
                    color: portalMessage ? "var(--red)" : "var(--blue)",
                    transition: "color var(--transition-default-time) ease-in-out",
                }}>
                    { portalMessage || "Powered by Stripe" }
                </div>
                <div css={{
                    fontSize: ".9em",
                    color: "lightgrey",
                }}>
                    Opens Stripe Portal where you can easily update subscription and manage payment methods.
                </div>
            </div>
        </div>
    );
};

const ActionButton = ({ name, success, message, processing, disabled, handleClick }) => (
    <div css={{
        fontSize: ".8em",
    }}>
        {
            typeof success !== "undefined" && !processing
            ?   <div css={{ marginBottom: 15 }}>
                    {
                        success === true
                        ?   <div css={{ color: "var(--green)" }}>{ message }</div>
                        :   success === false
                            ?   <div css={{ color: "var(--red)" }}>{ message }</div>
                            :   null
                    }
                </div>
            :   null
        }

        <button
            disabled={ processing || disabled }
            css={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "inherit",
                ":disabled": {
                    color: "var(--light)",
                    backgroundColor: "rgba(100, 100, 100, .2)",
                    cursor: "not-allowed",
                },
            }}
            onClick={ handleClick }
        >
            {
                processing
                ?   <RiLoader4Line
                        css={{
                            animation: `${keyframes({
                                from: {
                                    transform: "rotate(0deg)",
                                },
                                to: {
                                    transform: "rotate(360deg)",
                                },
                            })} .8s linear infinite`,
                        }}
                    />
                :   name
            }
        </button>
    </div>
);

const SettingsView = ({ code, me, org, updateOrg }) => {
    const stripe = useStripe();
    const elements = useElements();

    const [ processing, setProcessing ] = React.useState(false);
    const [ confirmMessage, setConfirmMessage ] = React.useState("");

    const [ codeSuccess, setCodeSuccess ] = React.useState(undefined);
    const [ codeMessage, setCodeMessage ] = React.useState(undefined);
    const [ orgSuccess, setOrgSuccess ] = React.useState(undefined);
    const [ orgMessage, setOrgMessage ] = React.useState(undefined);
    const [ customerSuccess, setCustomerSuccess ] = React.useState(undefined);
    const [ customerMessage, setCustomerMessage ] = React.useState(undefined);
    const [ paymentSuccess, setPaymentSuccess ] = React.useState(undefined);
    const [ paymentMessage, setPaymentMessage ] = React.useState(undefined);
    const [ discordWebhookSuccess, setDiscordWebhookSuccess ] = React.useState(undefined);
    const [ discordWebhookMessage, setDiscordWebhookMessage ] = React.useState(undefined);

    const [ orgCode, setOrgCode ] = React.useState("");
    const [ name, setName ] = React.useState("");
    const [ icon, setIcon ] = React.useState("");
    const [ email, setEmail ] = React.useState("");
    const [ address, setAddress ] = React.useState({});
    const [ discordWebhookID, setDiscordWebhookID ] = React.useState("");
    const [ discordWebhookToken, setDiscordWebhookToken ] = React.useState("");

    const testDiscordWebhook = () => {
        if (processing) return;

        setProcessing(true);

        if (!isBrowser()) return;

        window.fetch(process.env.API_URL + "/orgs/" + org._id + "/webhooks/discord/test", {
            method: "POST",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": me.token,
            },
        }).then(res => {
            if (res.ok) return res.json();
            throw new Error(res.statusText);
        }).then(() => {
            setProcessing(false);
        }).catch((e) => {
            console.error(e);
            setProcessing(false);
        });
    };

    const updateDiscordWebhook = () => {
        if (processing) return;

        setProcessing(true);

        if (!isBrowser()) return;

        window.fetch(process.env.API_URL + "/orgs/" + org._id + "/webhooks/discord", {
            method: "PUT",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": me.token,
            },
            body: JSON.stringify({
                id: discordWebhookID,
                token: discordWebhookToken,
            }),
        }).then(res => {
            if (res.ok) return res.json();
            throw new Error(res.statusText);
        }).then(orgDoc => {
            updateOrg({
                ...org,
                ...orgDoc,
            });
            setDiscordWebhookSuccess(true);
            setDiscordWebhookMessage("Successfully set the Discord Webhook.");
            setProcessing(false);
        }).catch((e) => {
            console.error(e);
            setDiscordWebhookSuccess(false);
            setDiscordWebhookMessage("Unable to set the Discord Webhook. Make sure it's valid and try saving it again. If the issue persists, please contact support.");
            setProcessing(false);
        });
    };

    const setValidOrgCode = (code) => {
        if (/^((?:[a-z])(?:[a-z0-9]+)?)?$/i.test(code)) {
            setOrgCode(code);
        }
    };

    const updateAddress = (key, value) => {
        setAddress({
            ...address,
            [key]: value,
        });
    };

    const toggleVisibility = () => {
        if (processing) return;

        setProcessing(true);

        if (!isBrowser()) return;

        window.fetch(process.env.API_URL + "/orgs/" + org._id + "/visibility", {
            method: "PATCH",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": me.token,
            },
        }).then(res => {
            if (res.ok) return res.json();
            throw new Error(res.statusText);
        }).then(orgDoc => {
            updateOrg({
                ...org,
                ...orgDoc,
            });
            setProcessing(false);
        }).catch((e) => {
            console.error(e);
            setProcessing(false);
        });
    };

    const updateOrgCodeDoc = () => {
        if (processing) return;

        // check validity of org code
        if (orgCode.length < 3 || orgCode.length > 16) {
            setCodeSuccess(false);
            setCodeMessage("Organization code should be within 3-16 characters.");
            return;
        }
        if (!/^(?:[a-z])(?:[a-z0-9]+)$/i.test(orgCode)) {
            setCodeSuccess(false);
            setCodeMessage("Organization code should only contain alphanumeric characters, and should start with an alphabet.");
            return;
        }

        setProcessing(true);

        if (!isBrowser()) return;

        window.fetch(process.env.API_URL + "/orgs/" + org._id + "/code", {
            method: "PATCH",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": me.token,
            },
            body: JSON.stringify({
                code: orgCode,
            }),
        }).then(res => {
            if (res.ok) return res.json();
            throw new Error(res.statusText);
        }).then(orgDoc => {
            updateOrg({
                ...org,
                ...orgDoc,
            });
            setCodeSuccess(true);
            setCodeMessage("Successfully changed the organization code.");
            setProcessing(false);
            navigate("/" + orgDoc.code + "/settings");
        }).catch((e) => {
            console.error(e);
            setCodeSuccess(false);
            setCodeMessage("Unable to change the organization code. " + e + ". Try saving it again. If the issue persists, please contact support.");
            setProcessing(false);
        });
    }

    const updateOrgDoc = () => {
        if (processing) return;

        if (name.length < 1 || name.length > 32) {
            setOrgSuccess(false);
            setOrgMessage("Organization name should be within 32 characters.");
            return;
        }
        if (icon && icon.length > 2048) {
            setOrgSuccess(false);
            setOrgMessage("Organization icon URL should be within 2048 characters.");
            return;
        }
        if (icon && !/^(((?:https:\/\/)(?:[-;:&=+$,\w]+@)?[A-Za-z0-9.-]+|(?:www\.|[-;:&=+$,\w]+@)[A-Za-z0-9.-]+)((?:\/[+~%/.\w\-_]*)?\??(?:[-+=&;%@.\w_]*)#?(?:[.!/\\\w]*))?\.(?:png|jpg|jpeg|bmp))$/i.test(icon)) {
            setOrgSuccess(false);
            setOrgMessage("Organization icon URL should be a valid URL to an image. Only PNG & JPEG images are supported.");
            return;
        }

        setProcessing(true);

        if (!isBrowser()) return;

        window.fetch(process.env.API_URL + "/orgs/" + org._id, {
            method: "PATCH",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": me.token,
            },
            body: JSON.stringify({
                name,
                icon,
            }),
        }).then(res => {
            if (res.ok) return res.json();
            throw new Error(res.statusText);
        }).then(orgDoc => {
            updateOrg({
                ...org,
                ...orgDoc,
            });
            setOrgSuccess(true);
            setOrgMessage("Successfully saved the organization details.");
            setProcessing(false);
        }).catch((e) => {
            console.error(e);
            setOrgSuccess(false);
            setOrgMessage("Unable to save the organization details. Try saving it again. If the issue persists, please contact support.");
            setProcessing(false);
        });
    }

    const updateCustomer = () => {
        if (processing) return;

        if (!/^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/i.test(email)) {
            setCustomerSuccess(false);
            setCustomerMessage("The email address entered by you is invalid.");
            return;
        }
        if (!address || !address.line1 || !address.city || !address.state || !address.postal_code || !address.country) {
            setCustomerSuccess(false);
            setCustomerMessage("Please enter the billing address.");
            return;
        }

        setProcessing(true);
        setCustomerMessage(undefined);

        if (!isBrowser()) return;

        window.fetch(process.env.API_URL + "/orgs/" + org._id + "/customer", {
            method: "PATCH",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": me.token,
            },
            body: JSON.stringify({
                email,
                address,
            }),
        }).then(res => {
            if (res.ok) return res.json();
            throw new Error(res.statusText);
        }).then(customer => {
            setCustomerSuccess(true);
            setCustomerMessage("Successfully saved the billing details.");
            updateOrg({
                ...org,
                stripeCustomerId: customer.id,
                customer,
            });
            setProcessing(false);
        }).catch((e) => {
            console.error(e);
            setCustomerSuccess(false);
            setCustomerMessage("Unable to save the billing details. Make sure the billing information is valid. If the issue persists, please contact support.");
            setProcessing(false);
        });
    };

    const updatePaymentMethod = () => {
        if (!stripe || !elements) return;
        if (processing) return;

        setProcessing(true);

        if (!isBrowser()) return;

        stripe.createPaymentMethod({
            type: "card",
            card: elements.getElement(CardElement),
        }).then(paymentMethodResult => {
            if (paymentMethodResult.error && paymentMethodResult.error.message) {
                setPaymentSuccess(false);
                setPaymentMessage(paymentMethodResult.error.message);
                setProcessing(false);
            } else {
                window.fetch(process.env.API_URL + "/orgs/" + org._id + "/payment-method", {
                    method: "PATCH",
                    headers: {
                        "Accept": "application/json",
                        "Content-Type": "application/json",
                        "Authorization": me.token,
                    },
                    body: JSON.stringify({
                        paymentMethodId: paymentMethodResult.paymentMethod.id,
                    }),
                }).then(res => {
                    if (res.ok) return res.json();
                    throw new Error(res.statusText);
                }).then(customer => {
                    setPaymentSuccess(true);
                    setPaymentMessage("Successfully saved the payment method.");
                    updateOrg({
                        ...org,
                        customer,
                    });
                    setProcessing(false);
                }).catch((e) => {
                    console.error(e);
                    setPaymentSuccess(false);
                    setPaymentMessage("Unable to save the payment method. Make sure the payment method is valid. If the issue persists, please contact support.");
                    setProcessing(false);
                });
            }
        }).catch((e) => {
            console.error(e);
            setPaymentSuccess(false);
            setPaymentMessage("Unable to save the payment method. Make sure the payment method is valid. If the issue persists, please contact support.");
            setProcessing(false);
        });
    };

    const manageSubscription = () => {
        if (!(org && org.stripeCustomerId && org.customer && org.customer.invoice_settings && org.customer.invoice_settings.default_payment_method)) return;

        if (!isBrowser()) return;

        window.fetch(process.env.API_URL + "/orgs/" + org._id + "/subscription", {
            method: "POST",
            headers: {
                "Accept": "application/json",
                "Content-Type": "application/json",
                "Authorization": me.token,
            },
            body: JSON.stringify({
                priceId: plans[0].id,
            }),
        }).then(res => {
            if (res.ok) return res.json();
            throw new Error(res.statusText);
        }).then(session => {
            setProcessing(false);
            window.location.href = session.url;
        }).catch((e) => {
            console.error(e);
            setProcessing(false);
        });
    };

    const managePaymentMethods = () => {
        manageSubscription();
    };

    const deleteOrg = () => {
        if (processing) return;

        setProcessing(true);
        setConfirmMessage("");

        if (!isBrowser()) return;

        window.fetch(process.env.API_URL + "/orgs/" + org._id, {
            method: "DELETE",
            headers: {
                "Accept": "application/json",
                "Authorization": me.token,
            },
        }).then(res => {
            if (res.ok) return res.json();
            throw new Error(res.statusText);
        }).then(() => {
            setProcessing(false);
            navigate("/account");
        }).catch((e) => {
            console.error(e);
            setProcessing(false);
        });
    };

    React.useEffect(() => {
        if (org.permissions === Permission.OWNER || org.permissions === Permission.ADMIN) {
            setOrgCode(org.code);
            setName(org.name);
            setIcon(org.icon || "");

            if (org.customer) {
                setEmail(org.customer.email);
                setAddress(org.customer.address);
            }

            if (org.notifications && org.notifications.discord) {
                setDiscordWebhookID(org.notifications.discord.id);
                setDiscordWebhookToken(org.notifications.discord.token);
            }
        } else {
            navigate("/" + org.code);
        }
    }, [ org ]);

    return (
        <div css={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
            maxWidth: 720,
            margin: "0 auto",
        }}>
            <Helmet
                title={ org.name + " - Settings" }
            />
            <div css={{ fontSize: "1.2em", margin: "25px 0" }}>Settings</div>

            <div css={{ color: "lightgrey", fontSize: ".9em", marginBottom: 5 }}>Organization Details</div>
            <div css={{ color: "darkgrey", fontSize: ".8em", marginBottom: 15 }}>Information required by us and stored on our servers.</div>
            <div css={{ width: "100%" }}>
                <div>
                    <Input.Text
                        title="Code"
                        description="A unique code to access your organization"
                        pretext={ <span css={{ fontFamily: "monospace" }}>{ isBrowser() ? window.location.host + "/" : "/" }</span> }
                        placeholder="e.g. glee"
                        value={ orgCode }
                        setValue={ setValidOrgCode }
                        maxLength={ 16 }
                        pattern="[a-zA-Z][a-zA-Z0-9]{2,15}"
                        required={ true }
                        disabled={ !(org.employee || (org.plan && org.plan.amount)) }
                    />
                </div>
                {
                    (org.employee || (org.plan && org.plan.amount))
                    ?   org.code !== orgCode
                        ?   <div css={{ color: "var(--red)", marginBottom: 15, fontSize: ".8em" }}>
                                If you change your code your previous code will become invalidated. This will change the URL for your organization. Make sure to notify your users about this change beforehand.
                            </div>
                        :   null
                    :   <div css={{ color: "var(--orange)", marginBottom: 15, fontSize: ".8em" }}>
                            Upgrade your subscription to set custom organization code.
                        </div>
                }
            </div>

            <ActionButton
                name={ "Update Code" }
                success={ codeSuccess }
                message={ codeMessage }
                processing={ processing }
                disabled={ !orgCode || org.code === orgCode }
                handleClick={ updateOrgCodeDoc }
            />

            <div css={{ width: "100%", marginTop: 25 }}>
                <div>
                    <Input.Text
                        title="Name"
                        description="Publicly visible name of your organization"
                        placeholder="e.g. Glee"
                        value={ name }
                        setValue={ setName }
                        maxLength={ 32 }
                        required={ true }
                    />
                    <Input.URL
                        title="Icon"
                        description="An https URL to the logo or icon of the organization"
                        placeholder="e.g. https://github.com/gleeapp.png"
                        value={ icon }
                        setValue={ setIcon }
                        pattern="https://.*"
                        maxLength={ 2048 }
                    />
                </div>
            </div>

            <ActionButton
                name={ "Save Details" }
                success={ orgSuccess }
                message={ orgMessage }
                processing={ processing }
                disabled={ !name || (name === org.name && (icon ? icon === org.icon : true)) }
                handleClick={ updateOrgDoc }
            />

            <div css={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginTop: 50,
            }}>
                <div>
                    <div css={{ color: "lightgrey", fontSize: ".9em", marginBottom: 5 }}>Public</div>
                    <div css={{ color: (org.employee || (org.plan && org.plan.amount)) ? "darkgrey" : "var(--orange)", fontSize: ".8em" }}>
                        {
                            (org.employee || (org.plan && org.plan.amount))
                            ?   org.visible
                                ?   "Anyone can view the organization."
                                :   "Only organization members can view the organization."
                            :   "Upgrade your subscription to enable Public visibility."
                        }
                    </div>
                </div>
                <Input.Toggle
                    enabled={ org.visible }
                    toggleHandler={ toggleVisibility }
                    disabled={ !(org.employee || (org.plan && org.plan.amount)) }
                />
            </div>

            <div css={{ color: "lightgrey", fontSize: ".9em", marginBottom: 5, marginTop: 50 }}>Discord Integration</div>
            <div css={{ color: "darkgrey", fontSize: ".8em", marginBottom: 15 }}>Connect to Discord Webhooks so that you can stay updated right within Discord. You'll receive a message in Discord when a new feedback is created, status of a feedback is changed, or when a feedback is deleted.</div>
            {
                (org.employee || (org.plan && org.plan.amount))
                ?   <>
                        <div css={{ width: "100%" }}>
                            <div>
                                <Input.Text
                                    title="Webhook ID"
                                    description="ID of the webhook you created in your Discord channel."
                                    placeholder="e.g. 691109880475287592"
                                    value={ discordWebhookID }
                                    setValue={ setDiscordWebhookID }
                                    maxLength={ 32 }
                                    pattern="[0-9]+"
                                />
                                <Input.Text
                                    title="Webhook Token"
                                    description="Token of the webhook you created in your Discord channel."
                                    placeholder="e.g. rgW6EeVmWPC5DL-iGJ2nHavc1vJvjjFEWWldIFj09jSwi6_fWNzEF9skpd909VgDcMZ5"
                                    value={ discordWebhookToken }
                                    setValue={ setDiscordWebhookToken }
                                    pattern="[a-zA-Z-._0-9]+"
                                />
                            </div>
                        </div>

                        <ActionButton
                            name={
                                org.notifications && org.notifications.discord && org.notifications.discord.id && org.notifications.discord.token && discordWebhookID === org.notifications.discord.id && discordWebhookToken === org.notifications.discord.token
                                ?   "Test Integration"
                                :   "Save Integration"
                            }
                            success={ discordWebhookSuccess }
                            message={ discordWebhookMessage }
                            processing={ processing }
                            disabled={ !discordWebhookID || !discordWebhookToken }
                            handleClick={
                                org.notifications && org.notifications.discord && org.notifications.discord.id && org.notifications.discord.token && discordWebhookID === org.notifications.discord.id && discordWebhookToken === org.notifications.discord.token
                                ?   testDiscordWebhook
                                :   updateDiscordWebhook
                            }
                        />
                    </>
                :   <div css={{ color: "var(--orange)", fontSize: ".8em" }}>
                        Upgrade your subscription to enable Discord Integration.
                    </div>
            }


            <div css={{ color: "lightgrey", fontSize: ".9em", marginBottom: 5, marginTop: 50 }}>Subscription Plan</div>
            <div css={{ color: "darkgrey", fontSize: ".8em", marginBottom: 15 }}>Select the plan suitable for your organization. Visit the Stripe Portal to upgrade your plan whenever you need it. Cancel anytime, no strings attached.</div>
            <div css={{ width: "100%" }}>
                <PlanSelector
                    planId={ org && org.stripeCustomerId && org.customer && org.customer.subscriptions && org.customer.subscriptions.data.length && org.customer.subscriptions.data[0].plan && org.customer.subscriptions.data[0].plan.id }
                    portalMessage={
                        org && org.stripeCustomerId && org.customer
                        ?   org.customer.invoice_settings && org.customer.invoice_settings.default_payment_method
                            ?   null
                            :   "Add a default payment method below before you can start your subscription."
                        :   "Update billing details below before you can start your subscription."
                    }
                    handleClick={ manageSubscription }
                />
            </div>

            <div css={{ color: "lightgrey", fontSize: ".9em", marginBottom: 5, marginTop: 50 }}>Billing Details</div>
            <div css={{ color: "darkgrey", fontSize: ".8em", marginBottom: 15 }}>
                Billing information required by our payment partner Stripe and stored on their servers. Only required when you want to upgrade from the Free plan.
            </div>
            <div css={{ width: "100%" }}>
                <div>
                    <Input.Email
                        title="Billing Email"
                        description="We'll send receipts to this inbox."
                        placeholder="e.g. billing@company.com"
                        value={ email }
                        setValue={ setEmail }
                        maxLength={ 512 }
                        pattern="[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+"
                        required={ true }
                    />
                    <Input.Text
                        title="Address"
                        placeholder="Street, PO Box, or company name."
                        value={ address.line1 }
                        setValue={ (value) => updateAddress("line1", value) }
                        maxLength={ 256 }
                        required={ true }
                    />
                    <Input.Text
                        title="City"
                        placeholder="City, district, suburb, town, or village."
                        value={ address.city }
                        setValue={ (value) => updateAddress("city", value) }
                        maxLength={ 64 }
                        required={ true }
                    />
                    <Input.Text
                        title="State"
                        placeholder="State, county, province, or region."
                        value={ address.state }
                        setValue={ (value) => updateAddress("state", value) }
                        maxLength={ 64 }
                        required={ true }
                    />
                    <Input.Text
                        title="Postal Code"
                        placeholder="ZIP or postal code."
                        value={ address.postal_code }
                        setValue={ (value) => updateAddress("postal_code", value) }
                        maxLength={ 8 }
                        required={ true }
                    />
                    <Input.Dropdown
                        title="Country"
                        placeholder="Country"
                        value={ address.country }
                        setValue={ (value) => updateAddress("country", value) }
                        options={ countries }
                        selectKey="value"
                        selectValue="key"
                        required={ true }
                    />
                </div>
            </div>

            <ActionButton
                name={ "Save Billing Details" }
                success={ customerSuccess }
                message={ customerMessage }
                processing={ processing }
                disabled={ !email || !address }
                handleClick={ updateCustomer }
            />

            {
                org && org.stripeCustomerId && org.customer
                ?   <>
                        <div css={{ color: "lightgrey", fontSize: ".9em", marginBottom: 5, marginTop: 50 }}>Payment Details</div>
                        <div css={{ color: "darkgrey", fontSize: ".8em", marginBottom: 15 }}>
                            Payment method required by our payment partner Stripe. You can update the payment method, or add additional ones, in the Stripe Portal whenever you want. Only required when you want to upgrade from the Free plan.
                        </div>
                        <div css={{ width: "100%" }}>
                            <div>
                                {
                                    !org.customer.invoice_settings || !org.customer.invoice_settings.default_payment_method
                                    ?   <div css={{
                                            marginBottom: 15,
                                            padding: 10,
                                            backgroundColor: "rgba(100, 100, 100, .2)",
                                            borderRadius: 3,
                                            transition: "background-color var(--transition-default-time) ease-out",
                                            ":hover": {
                                                backgroundColor: "rgba(100, 100, 100, .3)",
                                            },
                                            ":focus-within": {
                                                backgroundColor: "rgba(100, 100, 100, .3)",
                                            },
                                        }}>
                                            <div css={{
                                                marginBottom: 5,
                                                color: "rgba(200, 200, 200, .5)",
                                                fontSize: ".7em",
                                                fontWeight: 500,
                                                textTransform: "uppercase",
                                            }}>
                                                Card
                                            </div>
                                            <CardElement options={{
                                                style: {
                                                        base: {
                                                            fontFamily: "monospace",
                                                            color: "#f0f0f0",
                                                            iconColor: "#f0f0f0",
                                                            "::placeholder": {
                                                                color: "#c0c0c0",
                                                            },
                                                        },
                                                        invalid: {
                                                            color: "#ff453a",
                                                            iconColor: "#ff453a",
                                                        },
                                                },
                                            }} />
                                        </div>
                                    :   <div css={{
                                            marginBottom: 15,
                                            fontSize: ".9em",
                                            color: "var(--green)",
                                        }}>
                                            A default payment method is set for your organization. You can visit the Stripe Portal to manage your payment methods.
                                        </div>
                                }
                            </div>
                        </div>

                        <div css={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "space-between",
                        }}>
                            {
                                !org.customer.invoice_settings || !org.customer.invoice_settings.default_payment_method
                                ?   <ActionButton
                                        name={ "Save Payment Method" }
                                        success={ paymentSuccess }
                                        message={ paymentMessage }
                                        processing={ processing }
                                        disabled={ !email || !address }
                                        handleClick={ updatePaymentMethod }
                                    />
                                :   null
                            }

                            <ActionButton
                                name={ "Manage Payment Methods" }
                                processing={ processing }
                                disabled={ !(org && org.stripeCustomerId && org.customer && org.customer.invoice_settings && org.customer.invoice_settings.default_payment_method) }
                                handleClick={ managePaymentMethods }
                            />
                        </div>
                    </>
                :   null
            }

            <div css={{
                marginTop: 50,
                padding: 15,
                border: "2px solid var(--red)",
                borderRadius: 3,
                backgroundColor: "rgba(255, 69, 58, .1)",
            }}>
                <div css={{ fontSize: ".9em", marginBottom: 5 }}>Danger Zone</div>
                <div css={{ color: "lightgrey", fontSize: ".8em", marginBottom: 15 }}>When you delete your organization, all active subscriptions will be cancelled, all your organization data will be deleted (including posts, comments, votes, members, billing information, and payment method). This cannot be undone.</div>
                <div css={{ width: "100%" }}>
                    <button
                        css={{
                            fontSize: ".8em",
                            backgroundColor: "var(--red)",
                            color: "var(--light)",
                        }}
                        onClick={ () => setConfirmMessage("Are you sure you want to delete the " + org.name + " organization? This can't be undone.") }
                    >
                        Delete { org.name }
                    </button>
                </div>
            </div>

            {
                confirmMessage
                ?   <ConfirmDialog
                        title={ "Delete " + org.name }
                        message={ confirmMessage }
                        okHandler={ deleteOrg }
                        cancelHandler={ () => setConfirmMessage("") }
                    />
                :   null
            }
        </div>
    );
};

export default connect(
    state => ({ me: state.global.me, org: state.global.org }),
    dispatch => ({ updateOrg: org => dispatch(updateOrg(org)) }),
)(SettingsView);
